import { useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { IconButton, ThemeProviderV2 } from '@blueprint/ui';

import activityLogApi, {
    useActivityLogsList,
} from "../../api/userActivityLogs";
import { Loader } from "../../common/components/core";
import { useGeneralErrorHandlerCreator } from "../../common/hooks/useGeneralErrorHandler";
import { GenericError, composeErrorHandlers } from "../../utils/errorHandling";

import UserActivityLogList from "./components/UserActivityLogList";
import useFilterActivityLogHandler, {
    FilterActivityLogFormValues,
} from "./hooks/useFilterActivityLogHandler";
import { toDateTimeString } from "./utils/dateTime";
import { PageTitle } from "../../types/pageTitle";

export enum UserActivityLogActionType {
    CREATED_USER = "Created user",
    UPDATED_USER = "Updated user",
    DELETED_USER = "Deactivated User",
}

const UserActivityLogView = (): JSX.Element => {
    document.title = PageTitle.UserActivityLogView;
    const csvLink = useRef<any>();
    const [pageNo, setPageNo] = useState<number>(0);
    const pageSize = 10;

    const useGeneralError = useGeneralErrorHandlerCreator();

    const [csvData, setCsvData] = useState<any[]>([]);
    const [csvIsLoading, setCsvIsLoading] = useState<boolean>(false);
    const [filter, setFilter] = useState<FilterActivityLogFormValues>({
        actionTakerIds: [],
        activityType: "",
        dateFrom: null,
        dateTo: null,
    });

    const { show: showFilterHandler } = useFilterActivityLogHandler(
        filter,
        (values: FilterActivityLogFormValues) => {
            setFilter(values);
            setPageNo(0);
        },
    );

    const { data: activityLogs, isValidating: isActivityLogsLoading } =
        useActivityLogsList(
            filter?.actionTakerIds,
            filter?.activityType,
            filter?.dateFrom,
            filter?.dateTo,
            pageNo,
            pageSize,
        );

    // useEffect(() => {
    //     if (csvData && csvData.length) {
    //         setCsvData([]);
    //     }
    // }, [csvData]);

    const fetchFullLog = (event: any, done: any) => {
        setCsvIsLoading(true);
        try {
            // const res = await activityLogApi.export();
            activityLogApi.export().then(async (res) => {
                for (let item of res) {
                    item.activityTimestamp = toDateTimeString(
                        item.activityTimestamp,
                    );
                }
                setCsvData(res);
                csvLink?.current?.link?.click();
            });
        } catch (e) {
            composeErrorHandlers(useGeneralError)(e as GenericError);
        }
        setCsvIsLoading(false);
    };

    const nextPage = () => {
        if (activityLogs && !activityLogs.last) {
            setPageNo(pageNo + 1);
        }
    };
    const previousPage = () => {
        if (pageNo > 0) {
            setPageNo(pageNo - 1);
        }
    };
    const toPage = (page: number) => {
        if (activityLogs && page !== activityLogs.number) {
            if (page >= 0 && page < activityLogs.totalPages) {
                setPageNo(page);
            }
        }
    };

    const csvHeaders = [
        { label: "Activity", key: "activityType" },
        { label: "Action", key: "changedUserInfo" },
        { label: "Action Taker (email)", key: "actionedUser.email" },
        { label: "Action Taker (name)", key: "actionedUser.fullName" },
        { label: "Timestamp", key: "activityTimestamp" },
    ];

    const csvNameGenerator = () => {
        let nowDate = new Date();
        let dateString = nowDate
            .toLocaleString([], {
                year: "2-digit",
                month: "2-digit",
                day: "2-digit",
            })
            .replace("/", "-");

        return `ActivityLog_${dateString}_${nowDate.getTime()}`;
    };

    return (
        <>
            <ThemeProviderV2>
            <div className="d-flex align-items-center mb-4">
                <h1 className="flex-grow-1 mb-0">User Activity Log</h1>
                <div>
                    <CSVLink
                        ref={csvLink}
                        headers={csvHeaders}
                        data={csvData}
                        // filename={`ActivityLog${new Date()?.getTime()}.csv`}
                        filename={csvNameGenerator()}
                        style={{ display: "none" }}
                    >
                        <IconButton
                            icon="Download"
                            variant="light.tertiary">
                            Export
                        </IconButton>
                    </CSVLink>
                    <IconButton
                        icon="Download"
                        variant="light.tertiary"
                        onClick={fetchFullLog}
                        disabled={csvIsLoading}
                    >
                        Export
                    </IconButton>
                    {/* {csvData && csvData.length ? (
                        <CSVDownload
                            filename={`ActivityLog${new Date().getTime()}.csv`}
                            headers={csvHeaders}
                            data={csvData}
                        />
                    ) : null} */}
                    <IconButton
                        icon="Filter"
                        variant="light.primary"
                        onClick={showFilterHandler}
                        style={{marginLeft: '20px'}}
                    >
                        Filter
                    </IconButton>
                </div>
            </div>
            <Loader isLoading={isActivityLogsLoading} overlay={true}>
                {activityLogs && (
                    <UserActivityLogList
                        activityLogs={activityLogs}
                        nextPage={nextPage}
                        prevPage={previousPage}
                        toPage={toPage}
                    />
                )}
            </Loader>
            {/* ------------------------ use for testing -----------------------  */}
            {/* <UserActivityLogList
                        // activityLogs={activityLogs}
                    nextPage={nextPage}
                    prevPage={previousPage}
                    toPage={toPage}
             /> */}
            </ThemeProviderV2>
        </>
    );
};


export default UserActivityLogView;
